<template>
  <div
    class="reservation cursor-pointer flex justify-start items-center rounded px-3 py-2 shadow mb-2 text-sm h-10 hover:shadow-podHover transition-all duration-300 ease-in-out [&.isNew]:!bg-seats-seated [&.isNew]:!bg-opacity-10 bg-opacity-100"
    :key="reservation.id"
    :class="{
      'bg-link-primary text-fg-inverse': selected,
      'bg-surface-primary text-fg-primary': !selected,
      isNew,
    }"
    :id="`reservation-${reservation.id}`"
  >
    <svg v-if="!reservation.badge" viewBox="0 0 10 10" class="dot w-3 h-3 mr-2">
      <circle r="4" cx="5" cy="5" :class="badgeClasses" />
    </svg>
    <!--Remove this line after new performance page is out-->
    <div v-else class="dot w-3 h-3 rounded-full mr-2" :class="reservation.badge"></div>
    <span class="name font-sans capitalize">
      <template v-if="!reservation.contact || isWalkIn">
        <span class="font-semibold">Walk In</span>
      </template>
      <template v-else>
        <span class="font-semibold">{{ reservation.contact.last_name }}</span
        >, {{ reservation.contact.first_name }}
      </template>
    </span>
    <span class="flex items-center gap-4 pr-1 ml-auto">
      <template v-if="tags.length > 0">
        <TagDefault
          v-for="tag in tags"
          :key="tag.id"
          :id="`tag-${tag.id}`"
          :tag="tag"
          type="short"
        />
      </template>
      <span id="add-ons-indicator" v-if="hasAddOns">A</span>
      <span id="notes-indicator" v-if="hasNotes">
        <SpeechBubble />
      </span>
      <span id="show-guest-count" v-if="showGuestCount">
        {{ reservation.number_of_guests }}
      </span>
    </span>
  </div>
</template>

<!--Remove this section after new performance page is out-->
<style scoped>
.seated {
  background-color: rgb(119, 187, 77);
}

.arrived {
  background-color: rgb(11, 200, 250);
}

.no-show {
  background-color: rgb(255, 61, 25);
}
</style>

<script lang="ts">
import * as _ from 'lodash-es'
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import SpeechBubble from '@/assets/speechBubble.svg?component'
import TagDefault from '@/common/components/atoms/TagDefault.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { useSettingStore } from '@/stores/setting'

import {
  GuestTagRead,
  ReservationRead,
  ReservationStatus,
  ReservationTagRead,
} from '@generated/types'

export interface BadgedReservation extends ReservationRead {
  badge: string
  isNew: boolean
}

export default defineComponent({
  name: 'ReservationPodItem',
  components: {
    TagDefault,
    SpeechBubble,
  },
  props: {
    reservation: {
      type: Object as PropType<BadgedReservation>,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapStores(useSettingStore),
    ...mapState(usePerformanceReservationStore, ['selectedTableIds']),

    hasAddOns(): boolean {
      return this.reservation.add_ons.length > 0
    },

    hasNotes(): boolean {
      return (
        !!this.reservation.guest_notes ||
        !!this.reservation.internal_notes ||
        !!this.reservation.contact?.notes
      )
    },

    showGuestCount(): boolean {
      return this.reservation.number_of_guests > 0
    },

    isWalkIn(): boolean {
      return this.reservation.user.first_name === 'Walk' && this.reservation.user.last_name === 'In'
    },

    selected() {
      return (
        this.selectedTableIds.filter(selected => this.reservation.tables.includes(selected))
          .length > 0
      )
    },

    badgeClasses(): string {
      let badgeClasses: string
      switch (this.reservation.status) {
        case ReservationStatus.SEATED:
          badgeClasses = 'stroke-status-seated'
          break
        case ReservationStatus.ARRIVED:
          badgeClasses = 'stroke-status-waiting'
          break
        case ReservationStatus.EXPECTED:
          badgeClasses = 'stroke-status-expected'
          break
        case ReservationStatus.NO_SHOW:
          badgeClasses = 'stroke-status-noshow'
          break
        case ReservationStatus.UNPAID:
          badgeClasses = 'stroke-status-noshow'
          break
        case ReservationStatus.CLEARED:
        case ReservationStatus.CANCELED:
          badgeClasses = 'stroke-transparent'
          break
        default:
          badgeClasses = 'stroke-status-available'
      }
      return `${badgeClasses} fill-transparent stroke-2`
    },

    tags(): (GuestTagRead | ReservationTagRead)[] {
      const reservationTags = this.reservation.tags?.map(this.settingStore.getReservationTag) ?? []
      const guestTags = this.reservation.user?.tags?.map(this.settingStore.getGuestTag) ?? []
      return _.compact(_.concat(reservationTags, guestTags))
    },
  },
})
</script>
